import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Library, Search, ChevronRight, ArrowLeft, Clock, CheckCircle, AlertTriangle, Layers } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import Seo, { SITE_URL } from '../components/Seo';
import PaywallGate from '../components/PaywallGate';
import GUIDES from '../data/libraryGuides.json';

const TOTAL_GUIDES = GUIDES.length;

const CATEGORY_LABEL = {
  'onboarding':        'Onboarding',
  'getting-started':   'Getting Started',
  'strategy-dev':      'Strategy Development',
  'risk-mgmt':         'Risk Management',
  'analytics':         'Analytics',
  'tax':               'Tax & Accounting',
  'token-launch':      'Token Launch',
  'community':         'Community',
};

function GuideView({ slug }) {
  const guide = GUIDES.find((g) => g.guideId === slug);

  if (!guide) {
    return (
      <div className="min-h-screen bg-[#0a0b0f] text-white">
        <Seo
          title="Guide not found"
          description="The guide you were looking for does not exist on haythix."
          path={`/guides/${slug}`}
          noIndex
        />
        <PublicNav />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <h1 className="text-3xl font-bold mb-3">Guide not found</h1>
          <p className="text-gray-400 mb-6">The slug “{slug}” does not match any Library guide.</p>
          <Link to="/guides" className="text-amber-400 hover:text-amber-300 font-semibold inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to all guides
          </Link>
        </div>
      </div>
    );
  }

  const description = (guide.subtitle || guide.title || '').replace(/\s+/g, ' ').trim().slice(0, 200);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: guide.title,
      description,
      totalTime: guide.estimatedTime,
      step: (guide.steps || []).map((s) => ({
        '@type': 'HowToStep',
        position: s.stepNumber,
        name: s.title,
        text: s.description,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/guides/${slug}` },
      headline: guide.title,
      description,
      author: { '@type': 'Organization', name: 'haythix' },
      publisher: {
        '@type': 'Organization',
        name: 'haythix',
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/favicon.png` },
      },
      isAccessibleForFree: false,
      hasPart: {
        '@type': 'WebPageElement',
        isAccessibleForFree: false,
        cssSelector: '.haythix-paywalled-body',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home',    item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Guides',  item: `${SITE_URL}/guides` },
        { '@type': 'ListItem', position: 3, name: guide.title, item: `${SITE_URL}/guides/${slug}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#0a0b0f] text-white">
      <Seo
        title={guide.title}
        description={description}
        path={`/guides/${slug}`}
        jsonLd={jsonLd}
        type="article"
      />
      <PublicNav />

      <main className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/guides" className="text-xs text-gray-500 hover:text-amber-400 inline-flex items-center gap-1 mb-6">
          <ArrowLeft className="w-3 h-3" /> Back to all guides
        </Link>

        <header className="mb-8 border-b border-dark-700/50 pb-6">
          <p className="text-xs uppercase tracking-widest text-amber-400/80 mb-2">
            {CATEGORY_LABEL[guide.category] || guide.category}
          </p>
          <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-3">
            {guide.iconEmoji && <span className="mr-2">{guide.iconEmoji}</span>}
            {guide.title}
          </h1>
          {guide.subtitle && <p className="text-gray-400 text-lg mb-3">{guide.subtitle}</p>}
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
            {guide.estimatedTime && (
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {guide.estimatedTime}</span>
            )}
            {guide.difficulty && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-dark-700 text-gray-300 capitalize">
                {guide.difficulty}
              </span>
            )}
            {Array.isArray(guide.featuresUsed) && guide.featuresUsed.length > 0 && (
              <span className="inline-flex items-center gap-1"><Layers className="w-3 h-3" /> {guide.featuresUsed.length} features</span>
            )}
          </div>
        </header>

        <article className="haythix-paywalled-body space-y-8">
          <PaywallGate kind="guide">
            {Array.isArray(guide.outcomes) && guide.outcomes.length > 0 && (
              <section>
                <h2 className="text-xl font-semibold mb-3 text-white">What you'll have when finished</h2>
                <ul className="space-y-2">
                  {guide.outcomes.map((o, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                      <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /> {o}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {Array.isArray(guide.warnings) && guide.warnings.length > 0 && (
              <section className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
                <h2 className="text-sm font-semibold text-amber-300 mb-2 inline-flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Before you start
                </h2>
                <ul className="space-y-1.5">
                  {guide.warnings.map((w, i) => (
                    <li key={i} className="text-sm text-amber-100/90 leading-relaxed">• {w}</li>
                  ))}
                </ul>
              </section>
            )}

            {Array.isArray(guide.steps) && guide.steps.length > 0 && (
              <section>
                <h2 className="text-xl font-semibold mb-4 text-white">Walkthrough</h2>
                <ol className="space-y-5">
                  {guide.steps.map((step) => (
                    <li key={step.stepNumber} className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                        <span className="text-sm font-bold text-amber-400">{step.stepNumber}</span>
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base font-semibold text-white mb-1">{step.title}</h3>
                        <p className="text-sm text-gray-300 leading-relaxed">{step.description}</p>
                        {step.criteria && (
                          <p className="text-xs text-emerald-400/90 mt-1.5 inline-flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span>{Array.isArray(step.criteria) ? step.criteria.join(' · ') : step.criteria}</span>
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {guide.afterAction && (
              <section className="rounded-xl border border-dark-700/50 bg-dark-800/40 p-4">
                <h2 className="text-sm font-semibold text-white mb-2">What's next</h2>
                <p className="text-sm text-gray-300 leading-relaxed">
                  {typeof guide.afterAction === 'string' ? guide.afterAction : (guide.afterAction.content || '')}
                </p>
              </section>
            )}
          </PaywallGate>
        </article>
      </main>
      <PublicFooter />
    </div>
  );
}

function GuidesIndex() {
  const [search, setSearch] = useState('');

  const grouped = useMemo(() => {
    const groups = new Map();
    for (const g of GUIDES) {
      if (search) {
        const hay = `${g.title} ${g.subtitle || ''} ${g.category}`.toLowerCase();
        if (!hay.includes(search.toLowerCase())) continue;
      }
      const cat = g.category || 'other';
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat).push(g);
    }
    return [...groups.entries()].map(([cat, items]) => ({
      category: cat,
      label: CATEGORY_LABEL[cat] || cat,
      items: items.sort((a, b) => a.title.localeCompare(b.title)),
    }));
  }, [search]);

  return (
    <div className="min-h-screen bg-[#0a0b0f] text-white">
      <Seo
        title={`haythix Library — ${TOTAL_GUIDES} worked-example trading guides`}
        description={`Worked-example guides for crypto traders: ${TOTAL_GUIDES} step-by-step walkthroughs covering DEX bots, CEX trading, copy trading, AI signals, tokenomics, taxes, and more.`}
        path="/guides"
      />
      <PublicNav />

      <main className="max-w-5xl mx-auto px-6 py-12">
        <header className="mb-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-500/15 border border-amber-500/30 mb-4">
            <Library className="w-7 h-7 text-amber-400" />
          </div>
          <h1 className="text-4xl font-bold mb-3">Learning Library</h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            {TOTAL_GUIDES} worked-example guides — each one teaches a real workflow end-to-end, with the exact buttons and numbers to enter.
          </p>
        </header>

        <div className="relative max-w-xl mx-auto mb-10">
          <Search className="absolute top-1/2 left-4 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search guides…"
            className="w-full bg-dark-800 border border-dark-700 rounded-2xl pl-11 pr-4 py-3 text-sm text-white placeholder-gray-500 focus:border-amber-400 focus:outline-none"
          />
        </div>

        <div className="space-y-10">
          {grouped.map(({ category, label, items }) => (
            <section key={category}>
              <h2 className="text-xs uppercase tracking-widest text-amber-400/80 mb-4">{label}</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {items.map((g) => (
                  <Link
                    key={g.guideId}
                    to={`/guides/${g.guideId}`}
                    className="group flex items-start gap-3 p-4 rounded-xl border border-dark-700/50 bg-dark-800/40 hover:border-amber-500/30 hover:bg-dark-800 transition-all"
                  >
                    {g.iconEmoji && <span className="text-xl flex-shrink-0 mt-0.5">{g.iconEmoji}</span>}
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-white group-hover:text-amber-400 transition-colors mb-1">
                        {g.title}
                      </h3>
                      {g.subtitle && (
                        <p className="text-xs text-gray-500 line-clamp-2">{g.subtitle}</p>
                      )}
                      <div className="flex items-center gap-2 text-[10px] text-gray-600 mt-1.5">
                        {g.estimatedTime && <span>{g.estimatedTime}</span>}
                        {g.difficulty && <span className="capitalize">· {g.difficulty}</span>}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-amber-400 transition-colors flex-shrink-0 mt-0.5" />
                  </Link>
                ))}
              </div>
            </section>
          ))}

          {grouped.length === 0 && (
            <div className="text-center py-16 text-gray-500">
              No guides match “{search}”.
            </div>
          )}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}

export default function PublicGuidesPage() {
  const { slug } = useParams();
  return slug ? <GuideView slug={slug} /> : <GuidesIndex />;
}
